// @/utils/markdown_paste_handler.js
import { marked } from 'marked';
import { createHighlighter } from 'shiki';
import { CODE_LANGUAGES } from "@/editorjs/utils";

// --- 1. Shiki Highlighter를 모듈 스코프에서 비동기적으로 초기화 ---
let highlighter;

// 즉시 실행 함수(IIFE)를 사용하여 highlighter를 비동기적으로 설정
(async () => {
    try {
        // 테마와 언어는 필요한 것만 명시하여 번들 크기를 최적화할 수 있습니다.
        highlighter = await createHighlighter({
            themes: ['github-light', 'github-dark'],
            langs: CODE_LANGUAGES,
        });
    } catch (e) {
        console.error("Failed to initialize Shiki highlighter:", e);
        // highlighter 초기화 실패 시, shiki를 사용하지 않는 대체 로직을 제공할 수도 있습니다.
    }
})();

function escapeHtml(text) {
    return text.replace(/</g, "<").replace(/>/g, ">");
}

function tokensToEditorBlocks(tokens) {
    const blocks = [];

    // 중첩 리스트를 재귀적으로 처리하는 헬퍼 함수
    const buildListItems = (items) => {
        return items.map(item => {
            // 1. content 추출: 텍스트 타입 토큰만 골라 내용을 만듭니다.
            const content = item.tokens
                .filter(t => t.type === 'text' || t.type === 'html' || t.type === 'paragraph')
                .map(t => marked.parseInline(t.raw))
                .join(' ');

            // 2. 중첩된 items 추출: 모든 'list' 토큰을 찾습니다.
            const nestedListTokens = item.tokens?.filter(t => t.type === 'list');

            let nestedItems = [];
            if (nestedListTokens && nestedListTokens.length > 0) {
                // 3. 각 list 토큰의 items를 재귀적으로 처리하고, 그 결과를 하나의 배열로 합칩니다.
                //    flatMap은 map 실행 후 결과를 1단계 평탄화합니다.
                nestedItems = nestedListTokens.flatMap(listToken => buildListItems(listToken.items));
            }

            return {
                content: content.trim(),
                items: nestedItems,
            };
        });
    };

    for (const token of tokens) {
        let newBlocks = []; // 하나의 토큰에서 여러 블록이 생성될 수 있음

        switch (token.type) {
            case 'heading':
                const rawLevel = token.depth;
                const level = Math.min(Math.max(rawLevel, 2), 4);

                // --- 여기가 핵심 수정 부분 ---
                let headingText = '';

                // 1. heading 토큰 내부에 link, em, strong 등 인라인 토큰이 있는지 확인
                //    단순 텍스트만 있다면, 'tokens' 배열은 text 토큰 하나만 가집니다.
                //    (또는 'tokens' 자체가 없을 수도 있습니다.)
                const hasInlineElements = token.tokens && token.tokens.some(t => t.type !== 'text');

                if (hasInlineElements) {
                    // 2. 인라인 요소가 있다면, raw에서 #과 아랫줄을 모두 제거하고 파싱
                    //    Setext-style의 아랫줄을 제거하는 정규식 추가
                    const cleanedRaw = token.raw
                        .replace(/^#+\s*/, '')
                        .replace(/\n[=-]+$/, ''); // 마지막 줄의 === 또는 --- 제거
                    headingText = marked.parseInline(cleanedRaw.trim() || '');
                } else {
                    // 3. 인라인 요소가 없다면, 안전하게 token.text 사용
                    headingText = token.text;
                }

                newBlocks.push({
                    type: 'header',
                    data: {
                        text: headingText,
                        level: level
                    }
                });

                break;
            case 'paragraph':
                // --- 여기가 핵심 수정 부분 ---
                // paragraph 토큰 내의 모든 내부 토큰들을 순회합니다.
                let currentParaText = '';

                for (const innerToken of token.tokens || []) {
                    // 내부 토큰이 이미지인 경우
                    if (innerToken.type === 'image') {
                        // 1. 이미지 이전에 축적된 텍스트가 있다면, 먼저 paragraph 블록으로 만듭니다.
                        if (currentParaText.trim()) {
                            newBlocks.push({ type: 'paragraph', data: { text: currentParaText.trim() } });
                        }
                        currentParaText = ''; // 텍스트 버퍼 비우기

                        // 2. 이미지 블록을 별도로 추가합니다.
                        newBlocks.push({
                            type: 'image',
                            data: {
                                file: { url: innerToken.href },
                                caption: innerToken.text,
                                link: '', withBorder: false, withBackground: true, withoutGap: false
                            }
                        });
                    }
                    // 링크 안에 이미지가 있는 경우도 처리
                    else if (innerToken.type === 'link' && innerToken.tokens?.length === 1 && innerToken.tokens[0].type === 'image') {
                        if (currentParaText.trim()) {
                            newBlocks.push({ type: 'paragraph', data: { text: currentParaText.trim() } });
                        }
                        currentParaText = '';
                        const imageToken = innerToken.tokens[0];
                        newBlocks.push({
                            type: 'image', data: { file: { url: imageToken.href }, caption: imageToken.text, link: innerToken.href, withBorder: false, withBackground: true, withoutGap: false }
                        });
                    }
                    else {
                        // 이미지가 아니면, 원본 텍스트를 버퍼에 추가합니다.
                        currentParaText += innerToken.raw;
                    }
                }

                // 3. 루프가 끝난 후, 버퍼에 남은 텍스트가 있다면 마지막으로 paragraph 블록을 만듭니다.
                if (currentParaText.trim()) {
                    newBlocks.push({ type: 'paragraph', data: { text: marked.parseInline(currentParaText.trim()) } });
                }
                break;
            case 'code':
                let highlighted_html = '';
                if (highlighter) {
                    try {
                        highlighted_html = highlighter.codeToHtml(token.text, {
                            lang: token.lang || 'plaintext',
                            theme: document.documentElement.classList.contains('dark') ? 'github-dark' : 'github-light'
                        });
                    } catch (e) { highlighted_html = `<pre><code>${escapeHtml(token.text)}</code></pre>`; }
                } else { highlighted_html = `<pre><code>${escapeHtml(token.text)}</code></pre>`; }

                newBlocks.push({
                    type: 'ShikiCodeTool',
                    data: { code: token.text, language: token.lang || 'plaintext', highlighted_html }
                });
                break;
            case 'list':
                // 이 부분이 핵심입니다. lexer는 마커가 달라도 하나의 'list' 토큰으로 묶어줍니다.
                newBlocks.push({
                    type: 'list',
                    data: {
                        style: token.ordered ? 'ordered' : 'unordered',
                        items: buildListItems(token.items),
                    }
                });
                break;
            case 'blockquote':
                newBlocks.push({ type: 'quote', data: { text: marked.parseInline(token.text || ''), caption: '', alignment: 'left' } });
                break;
            case 'hr':
                newBlocks.push({ type: 'delimiter', data: {} });
                break;
            case 'table':
                newBlocks.push({
                    type: 'table',
                    data: {
                        withHeadings: true,
                        content: [
                            token.header.map(cell => marked.parseInline(cell.text || '')),
                            ...token.rows.map(row => row.map(cell => marked.parseInline(cell.text || '')))
                        ]
                    }
                });
                break;
        }

        if (newBlocks.length > 0) {
            blocks.push(...newBlocks); // 생성된 블록(들)을 최종 배열에 추가
        }
    }
    return blocks;
}

function validateMarkdownText(rawText = "") {
    // 너무 짧거나 유효하지 않은 텍스트는 조기 반환
    if (!rawText || typeof rawText !== 'string' || rawText.trim().length < 5) {
        return false;
    }

    // 1. 빠른 경로: 블록 레벨 마크다운 패턴이 한 줄이라도 시작 부분에 있으면 즉시 true 반환
    // 기존 로직을 개선하여 순서 있는 리스트(\d+\.\s), 테이블(|)도 추가
    const blockPattern = /^(#+\s|\d+\.\s|\*\s|-\s|>\s|`{3}|\|.*\||---\s*$)/m;
    if (blockPattern.test(rawText)) {
        return true;
    }

    // 2. 점수 기반 추론: 텍스트 전체에서 마크다운 특징을 찾아 점수를 매김
    let score = 0;

    // 링크: [text](url) - 매우 강력한 신호 (개당 +2점)
    const links = rawText.match(/\[([^\]]+)\]\([^\)]+\)/g) || [];
    score += links.length * 2;

    // 볼드: **text** - 강력한 신호 (개당 +1점)
    const bolds = rawText.match(/\*\*([^\*]+)\*\*/g) || [];
    score += bolds.length;

    // 이탤릭: *text* 또는 _text_ - 비교적 약한 신호 (개당 +0.5점)
    const italics = rawText.match(/(?<!\*)\*([^\*]+)\*(?!\*)|_([^_]+)_/g) || [];
    score += italics.length * 0.5;

    // Setext 스타일 헤더 (밑줄 --- 또는 ===) - 강력한 신호 (개당 +2점)
    const setextHeaders = rawText.match(/^(.+)\n[=-]{3,}\s*$/gm) || [];
    score += setextHeaders.length * 2;

    // 빈 줄로 분리된 단락 수 (구조적 복잡성을 나타냄)
    // 2개 이상의 단락이 있다면, 추가 단락마다 +1점
    const paragraphs = rawText.split(/\n\s*\n/).filter(p => p.trim() !== '').length;
    if (paragraphs > 1) {
        score += (paragraphs - 1);
    }

    /*
     * 예제 텍스트 분석:
     * **Omarchy** -> bold 1개 (score = 1)
     * [YouTube로 듣기](...) -> link 1개 (score = 1 + 2 = 3)
     * 3개의 빈 줄 -> 4개 단락 (score = 3 + (4-1) = 6)
     * 최종 점수: 6. 임계값(2)을 훨씬 넘으므로 true.
    */

    // 임계값(Threshold) 설정: 점수가 2 이상이면 마크다운으로 간주
    // (예: 링크 1개, 또는 볼드 2개, 또는 볼드 1개 + 단락 2개 등)
    return score >= 2;
}

export function convertMarkdownToBlocks(markdownText) {
    const normalized = markdownText.replace(/^ {0,3}```\t/gm, '```');
    const tokens = marked.lexer(normalized.trim(), { gfm: true });

    // 토큰을 Editor.js 블록으로 변환
    return tokensToEditorBlocks(tokens);
}

export async function processMarkdownPasteEvent(editor, event) {
    const target = event.target;

    // 1. 이벤트 타겟이 'textarea'나 'input'이면 핸들러를 실행하지 않고 즉시 종료합니다.
    //    이를 통해 MarkdownTool, ShikiCodeTool, RawTool 등의 내부 붙여넣기가 정상 동작합니다.
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') {
        return;
    }

    // 2. 이벤트 타겟이 contenteditable이 아닌 요소(예: 버튼)에서 발생한 경우도 무시합니다.
    if (!target.isContentEditable) {
        // 단, Editor.js의 메인 편집 영역(redactor) 자체는 contenteditable이 아닐 수 있으므로,
        // 그 안의 자식 요소에서 발생하는 것을 허용하기 위해 좀 더 정교한 체크가 필요할 수 있습니다.
        // 아래 .closest() 체크로 대부분의 경우를 커버할 수 있습니다.
        return;
    }

    if (target.closest('.markdown-tool, .ce-code, .ce-rawtool')) {
        return;
    }

    const pastedText = (event.clipboardData || window.clipboardData).getData('text/plain');

    if (!validateMarkdownText(pastedText)) {
        return;
    }

    event.preventDefault();
    event.stopPropagation();

    try {
        await editor.isReady;

        const newBlocks = convertMarkdownToBlocks(pastedText)

        if (newBlocks.length > 0) {
            const currentBlockIndex = editor.blocks.getCurrentBlockIndex();
            editor.blocks.insertMany(newBlocks, undefined, currentBlockIndex + 1);

            const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);

            if (currentBlock && currentBlock.isEmpty) {
                editor.blocks.delete(currentBlockIndex);
                editor.caret.setToBlock(currentBlockIndex, 'start');
            } else {
                editor.caret.setToBlock(currentBlockIndex + 1, 'start');
            }
        }
    } catch (e) {
        console.error("Markdown paste processing failed:", e);
    }
}